import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { logger } from '@/features/help/utils/logger';
import api from '@/features/api/axios';
import { useApplicationStore } from '../../store/application.store';

interface TermsConsentFieldProps {
  onChange: (checked: boolean) => void;
  value: boolean;
  disabled?: boolean;
  isInvalid?: boolean;
}

/**
 * Renders the required terms and consent field that appears at the end of every form
 */
export const TermsConsentField = ({
  onChange,
  value,
  disabled = false,
  isInvalid = false,
}: TermsConsentFieldProps) => {
  const [brokerName, setBrokerName] = useState<string>('');
  // Get shortId from the application store for linking to legal pages
  const { userShortId } = useApplicationStore();

  // Fetch company name from the API
  useEffect(() => {
    async function fetchCompanyName() {
      try {
        const response = await api.get('/api/legal/company-info');
        if (response.data && response.data.company_name) {
          setBrokerName(response.data.company_name);
        }
      } catch (error) {
        logger.error('TermsConsentField', 'Failed to fetch company name', {
          error,
        });
      }
    }

    fetchCompanyName();
  }, []);

  // Render validation error message if field is invalid
  const renderValidationError = () => {
    if (!isInvalid) return null;

    return (
      <div className="mt-1 text-sm text-red-600 flex items-center">
        <AlertCircle className="h-4 w-4 mr-1" />
        You must agree to the terms to continue
      </div>
    );
  };

  return (
    <div className="mb-4">
      <label
        className={`flex items-start space-x-2 text-sm font-medium ${disabled ? 'text-gray-500 cursor-not-allowed' : 'text-gray-700'}`}
      >
        <input
          type="checkbox"
          className={`mt-1 h-4 w-4 ${
            disabled
              ? 'text-gray-400 border-gray-200 bg-gray-100 cursor-not-allowed'
              : 'text-blue-600 border-gray-300'
          } ${isInvalid ? 'border-red-500 ring-1 ring-red-500' : ''} focus:ring-blue-500 rounded`}
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          required={true}
          disabled={disabled}
          data-testid="terms-consent-checkbox"
        />
        <span className="flex-1">
          <div className="text-gray-700">
            By clicking Submit, you agree to the{' '}
            <Link
              to={`/legal/terms?shortId=${userShortId || ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Terms of Use
            </Link>{' '}
            and acknowledge the{' '}
            <Link
              to={`/legal/privacy?shortId=${userShortId || ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link
              to={`/legal/disclaimer?shortId=${userShortId || ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Disclaimer
            </Link>
            , and you consent to {brokerName || '[Broker Name]'} and BigTuna processing your
            personal information for the purpose of evaluating your application.
            <span className="text-red-500">*</span>
          </div>
        </span>
      </label>
      {renderValidationError()}
    </div>
  );
};
